/**
 * Timeline -> OPBridge auto-sync (2026-08-21).
 *
 * WHY THIS EXISTS. `bridgeSync.ts::pushTimeline` already reaches OPBridge's
 * `PUT /timelines/{consumer}/{id}` - but until now that only ever fired
 * right before a Launch/fire, or via the manual "Push to OPBridge" button.
 * Between those moments, an edited timeline lived in localStorage only,
 * with no OPBridge-side copy reflecting the latest changes. Operator ask:
 * edits should reach OPBridge continuously while authoring, not just at
 * Launch time, with a visible confirmation that it actually landed.
 *
 * OPBridge's own handler ALREADY persists every push to disk under this
 * user's OPButtons-scoped data dir (`handlers/timelines.rs::upsert_instance`
 * calls `timelines_wiring::persist_timelines` unconditionally, confirmed by
 * reading OPBridge's source directly, not assumed) - so a successful push
 * here already means "saved to disk AND the live engine has it," no
 * separate doc-store write needed. This module's only job is triggering
 * that push proactively, on edit, instead of waiting for Launch.
 *
 * Deliberately debounced, not per-keystroke - a keyframe drag fires many
 * times a second; pushing on every tick would hammer OPBridge with writes
 * obsolete before the round-trip completes. Same shape as
 * `rigState/rigStateSync.ts` (1.5s debounce, JSON-diffed against the last
 * push so a no-op edit never re-sends) - reused deliberately rather than
 * inventing a second pattern, just retargeted at the already-working
 * engine-push path instead of the generic doc store (Timeline data has its
 * own dedicated, already-persisting endpoint; routing it through
 * `/docs/opbuttons/*` too would just be a second copy that can drift).
 *
 * TARGET: the timeline's HOME bridge (2026-09-29; was "primary only", from
 * DEC-177 following DEC-063). A timeline plays on the machine its devices are
 * on - see `bridgeSync.ts::timelinePlacement` - so that is where its copy
 * must live. Measured that day: a Helix timeline edited in a desktop browser
 * whose primary is the Gaming-PC was being pushed to the Gaming-PC, which has
 * no Helix port, while the Helix sits on the Mac Studio.
 *
 * When a timeline's home MOVES (a Helix step added to a timeline that used to
 * play on the PC, say), the copy left on the old machine is deleted here, so
 * no machine keeps a stale copy a route or an old launcher could still start.
 * The record of where copies may remain is kept in localStorage, so an old
 * machine that is offline at the moment is cleaned up the next time it is
 * reachable, not forgotten.
 *
 * 2026-08-23 latency pass - Launch no longer unconditionally re-pushes.
 * `bridgeSync.ts` push calls used to fire on EVERY Launch press regardless
 * of whether this module had already landed the identical content seconds
 * earlier (via its own debounce, or via the manual "Push to OPBridge"
 * button) - a wasted round-trip on the most gig-critical, most frequently
 * pressed action in the app. `pushedJson` is keyed by
 * `(timelineId, resolved bridge id)`, not timeline id alone - a fan-out
 * Launch (an item's `bridgeIds` naming several bridges) could otherwise
 * skip pushing to a SECOND target this module never actually reached, just
 * because the FIRST target's content happened to match.
 * `isTimelinePushCurrent()` lets a caller check per-target before deciding
 * whether to push; `notePushOutcome()` takes the target that was actually
 * pushed, so Launch's own successful pushes update the SAME tracker autoSync
 * reads, keeping every push path (auto-debounce, manual button, Launch)
 * consistent about what OPBridge already has.
 */

import { createEffect, createRoot, createSignal, on, untrack } from 'solid-js';
import { bridges, getConnection, primaryBridge, resolveBridge } from '../opbridge/store.ts';
import { activeProject } from '../layout/projectStore.ts';
import { getTimeline, timelines } from './store.ts';
import { timelineRunning, transportRun } from './transport.ts';
import type { BridgeConfig } from '../opbridge/storage.ts';
import { deleteTimelineOnBridge, knownPortsFor, pushTimeline, timelineParts, timelinePlacement, toBridgeTimeline, type TimelinePart } from './bridgeSync.ts';
import { plainFireReason, type FireResult } from '../opbridge/dispatch.ts';
import { timelineLoops, type Timeline } from '@opbuttons/schema';

const DEBOUNCE_MS = 1500;

export type TimelineSyncState = 'idle' | 'pending' | 'saving' | 'saved' | 'error';

interface TimelineSyncEntry {
  state: TimelineSyncState;
  lastSavedAt: number | null;
  lastError: string | null;
  /** What the last push said won't play (steps dropped, ports missing), or
   *  `null`. Shown by the sync pill so a problem is visible while authoring,
   *  not only in a Launch toast (2026-09-29). */
  lastWarning: string | null;
}

const [entries, setEntries] = createSignal<Record<string, TimelineSyncEntry>>({});
const timers = new Map<string, ReturnType<typeof setTimeout>>();
/** Keyed by `${timelineId}::${resolved bridge id}` - see the module doc
 *  comment's 2026-08-23 note for why target is part of the key. */
const pushedJson = new Map<string, string>();
const pushInFlight = new Set<string>();
/** A push asked for while one was in flight: sent again (the latest version) when that one lands (2026-10-16: it was
 *  dropped, so the last edit of a quick run could stay unsent with the pill stuck on pending). */
const pushAgain = new Set<string>();
/** Timelines with a part on a computer that was offline when their push was due: sent when it is back. */
const waitingForHome = new Set<string>();

/** A dispatch `target` string (an item's `bridgeIds` entry, or `''`/
 *  undefined meaning "the default bridge") resolves to a CONCRETE bridge id
 *  the same way `opbridge/store.ts::resolveBridge` does for real dispatch -
 *  falsy resolves to the primary. Returns `null` when there's no primary to
 *  fall back to (nothing to compare against; callers must treat that as
 *  "not current," i.e. always push). */
function resolveTargetId(target: string): string | null {
  if (target) return target;
  return primaryBridge()?.id ?? null;
}

function pushedKey(timelineId: string, resolvedTargetId: string): string {
  return `${timelineId}::${resolvedTargetId}`;
}

/** The exact bytes a push to `resolvedTargetId` would PUT, or `null` when
 *  that bridge is not in the store (caller must treat as "not current").
 *
 *  2026-09-17 - this used to be `JSON.stringify(t)`, and that was the bug.
 *  A MIDI port is NOT part of a Timeline: `bridgeSync.ts::resolveStepToCues`
 *  injects it at push time from `pedal?.port ?? cfg.defaultMidiOutput`. So
 *  comparing the SOURCE timeline made the cache blind to every port change,
 *  and a Launch after one skipped the push and left OPBridge firing the
 *  PREVIOUS port - which `rules_wiring` drops silently, so the operator gets
 *  no sound and no error. Comparing the WIRE instead makes the cache answer
 *  the question it always claimed to ("does OPBridge already have this
 *  content"). `toBridgeTimeline` is pure and local - no network.
 *
 *  Pinned by `smokes/timeline_push_not_skipped_after_port_change.ts`. The
 *  port list only ever adds a WARNING, never changes the wire, so a stale
 *  cached list cannot make this skip a push that was needed. */
function wireJsonFor(t: Timeline, resolvedTargetId: string): string | null {
  const cfg = bridges().find((b) => b.id === resolvedTargetId);
  if (!cfg) return null;
  return JSON.stringify(toBridgeTimeline(t, cfg, { followsHere: true, knownPorts: knownPortsFor(cfg.id) }).wire);
}

/** True when OPBridge already has this EXACT timeline content for this
 *  target, per the last confirmed push THIS tab observed (any of: the
 *  debounced auto-push, the manual "Push to OPBridge" button, or a prior
 *  Launch) - i.e. it's safe to skip pushing again before firing/starting.
 *  Conservative on anything uncertain: an unresolvable target (no primary)
 *  returns `false`, never assumed current. */
export function isTimelinePushCurrent(t: Timeline, target: string): boolean {
  const resolved = resolveTargetId(target);
  if (resolved === null) return false;
  const json = wireJsonFor(t, resolved);
  if (json === null) return false;
  return pushedJson.get(pushedKey(t.id, resolved)) === json;
}

/** The exact bytes one computer's PART is sent (`bridgeSync.ts::timelineParts`). */
function partWire(p: TimelinePart): string {
  return JSON.stringify(toBridgeTimeline(p.def, p.cfg, { followsHere: p.followsHere, siblings: p.siblings, knownPorts: knownPortsFor(p.cfg.id) }).wire);
}

/** True when every connected computer holding a part of `t` already has exactly its part, and no connected computer
 *  that should not hold `t` any more still does (that needs a push round to clean it up). */
export function timelinePartsCurrent(t: Timeline): boolean {
  const { parts } = timelineParts(t);
  const live = parts.filter((p) => connected(p.cfg));
  if (live.length === 0) return false;
  if (!live.every((p) => pushedJson.get(pushedKey(t.id, p.cfg.id)) === partWire(p))) return false;
  return staleHolders(t, parts).every((key) => !connected(resolveBridge(key) ?? null));
}

/** Push `t` to every connected computer holding a part (2026-10-28, "the whole rig follows"): only the parts that are
 *  not already there, unless `live` (which also replaces a running copy, S306). Then the copy is deleted from any
 *  computer that should not hold it any more. A computer that is off gets its part when it is back. One result per
 *  computer pushed; none when every part was already current. `liveCopies` sums the running copies replaced. */
export async function pushTimelineParts(t: Timeline, opts: { live?: boolean; force?: boolean } = {}): Promise<{ results: FireResult[]; liveCopies?: number }> {
  const { parts } = timelineParts(t);
  if (parts.some((p) => !connected(p.cfg))) waitingForHome.add(t.id); else waitingForHome.delete(t.id);
  // `force`: every part, current or not (the Legacy editor's Save button).
  const due = parts.filter((p) => connected(p.cfg) && (opts.live || opts.force || pushedJson.get(pushedKey(t.id, p.cfg.id)) !== partWire(p)));
  const sent = await Promise.all(due.map(async (p) => {
    const results = await pushTimeline(p.def, p.cfg.id, { followsHere: p.followsHere, siblings: p.siblings, ...(opts.live ? { live: true } : {}) });
    // Remembered only when it landed and nothing newer was kept there (S324).
    if (results.length > 0 && results.every((r) => r.kind === 'ok' && r.newerCopy === undefined)) pushedJson.set(pushedKey(t.id, p.cfg.id), partWire(p));
    return results;
  }));
  const results = sent.flat();
  if (results.every((r) => r.kind === 'ok')) await cleanUpStaleCopies(t, parts);
  const copies = results.filter((r) => r.liveCopies !== undefined);
  return copies.length > 0 ? { results, liveCopies: copies.reduce((n, r) => n + (r.liveCopies ?? 0), 0) } : { results };
}

/** Forget that this tab pushed `id` anywhere, so the next launch pushes it again (OPBridge said it does not hold it,
 *  `withRepush` in launch.ts). */
export function forgetTimelinePush(id: string): void {
  for (const key of [...pushedJson.keys()]) if (key.startsWith(`${id}::`)) pushedJson.delete(key);
}

function entryFor(id: string): TimelineSyncEntry {
  return entries()[id] ?? { state: 'idle', lastSavedAt: null, lastError: null, lastWarning: null };
}

function patchEntry(id: string, patch: Partial<TimelineSyncEntry>): void {
  setEntries((prev) => ({ ...prev, [id]: { ...entryFor(id), ...patch } }));
}

/** Reactive sync state for one timeline id - `'idle'` (nothing pushed yet
 *  this session), `'pending'` (edited, debounce running), `'saving'` (push
 *  in flight), `'saved'` (OPBridge confirmed 200 - on disk + live engine),
 *  `'error'` (last push failed; see {@link timelineSyncLastError}). */
export function timelineSyncState(id: string): TimelineSyncState {
  return entryFor(id).state;
}

/** `Date.now()` of the last confirmed save, or `null` if never saved this
 *  session. */
export function timelineSyncLastSavedAt(id: string): number | null {
  return entryFor(id).lastSavedAt;
}

/** Free-form detail from the last failed push, or `null`. */
export function timelineSyncLastError(id: string): string | null {
  return entryFor(id).lastError;
}

/** What the last push said won't play, or `null` (2026-09-29). */
export function timelineSyncWarning(id: string): string | null {
  return entryFor(id).lastWarning;
}

function connected(cfg: BridgeConfig | null): cfg is BridgeConfig {
  if (!cfg) return false;
  const conn = getConnection(cfg.id);
  return !!conn && conn.status() === 'connected';
}

/** Apply the outcome of a push (from ANY caller) to this timeline's shared
 *  sync state. Factored out so a caller that already ran its own
 *  `pushTimeline` (the manual "Push to OPBridge" button, which needs the
 *  raw `FireResult[]` for its own toast wording) still updates the SAME
 *  state the auto-sync indicator reads - one source of truth regardless of
 *  which path actually pushed. `target` is the literal string that was
 *  passed to `pushTimeline` (possibly `''`/default) - resolved to a
 *  concrete bridge id before keying `pushedJson`, so every push path agrees
 *  on the same key. The UI-facing sync state (`entries`, keyed by timeline
 *  id alone) is a whole-timeline indicator, not per-bridge. */
function applyPushOutcome(t: Timeline, target: string, results: FireResult[]): boolean {
  // OPBridge kept a NEWER copy (S324): nothing of ours landed, so it must not be remembered as pushed (a later launch
  // would skip pushing); the library sync brings the newer copy here.
  if (results.some((r) => r.newerCopy !== undefined)) {
    patchEntry(t.id, { state: 'idle', lastError: null, lastWarning: 'The computer has a newer copy of this timeline; this device updates when it syncs.' });
    return false;
  }
  const fail = results.find((r) => r.kind !== 'ok');
  if (fail) {
    // `unsupported-step` means nothing on this timeline resolves to a
    // fireable action on that bridge - an empty in-progress draft (DEC-153:
    // fresh timelines start with zero lanes), or an unsupported tier. Not a
    // sync failure worth a red pill while mid-author. But when the timeline
    // HAS steps and none can play there, the reason is exactly what the
    // operator needs to see, so it is kept as a warning (2026-09-29).
    if (fail.kind === 'unsupported-step') {
      const hasSteps = t.lanes.some((lane) => lane.steps.length > 0);
      patchEntry(t.id, { state: 'idle', lastError: null, lastWarning: hasSteps ? (fail.detail ?? null) : null });
      return false;
    }
    patchEntry(t.id, { state: 'error', lastError: plainFireReason(fail) });
    return false;
  }
  const resolved = resolveTargetId(target);
  if (resolved !== null) {
    // Record the WIRE we just sent, not the source timeline - see
    // `wireJsonFor`. Storing the source here would reintroduce the
    // stale-port skip from the write side even with the read side fixed.
    const json = wireJsonFor(t, resolved);
    if (json !== null) pushedJson.set(pushedKey(t.id, resolved), json);
  }
  const warning = results.find((r) => r.partialDropWarning)?.partialDropWarning ?? null;
  patchEntry(t.id, { state: 'saved', lastSavedAt: Date.now(), lastError: null, lastWarning: warning });
  return true;
}

/** Record that a push for this timeline has started (any caller) - flips
 *  the indicator to `saving` immediately rather than waiting for the
 *  round-trip. */
export function notePushStarted(id: string): void {
  patchEntry(id, { state: 'saving' });
}

/** Record the outcome of a push this module didn't itself perform - the
 *  manual "Push to OPBridge" button in `TimelineEditor.tsx`, or a Launch
 *  that just pushed before firing/starting (`TimelineLauncher.tsx`/
 *  `TimelineEditor.tsx`'s Launch handlers, which pass the actual target(s)
 *  they pushed to). See {@link applyPushOutcome}. Also runs the home-move
 *  cleanup when the push went to the timeline's home. */
export function notePushOutcome(t: Timeline, results: FireResult[], target = ''): void {
  applyPushOutcome(t, target, results);
}

// ─── Home-move cleanup (2026-09-29; every part's computer since 2026-10-28) ─

/** localStorage: timeline id -> where its home copy lives, plus machines that
 *  may still hold an older copy. Keys are node ids when known (stable across
 *  devices and re-pairs), else row ids; `resolveBridge` takes either. */
const HOMES_KEY = 'opbuttons.timelineHomes.v1';

interface HomeRecord {
  home: string;
  /** Every machine meant to hold a part (2026-10-28); absent on a record written before, which held the home alone. */
  holders?: string[];
  /** Machines that held this timeline before its home moved, not yet cleaned. */
  stale: string[];
}

function readHomes(): Record<string, HomeRecord> {
  try {
    const raw = localStorage.getItem(HOMES_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : {};
    return parsed && typeof parsed === 'object' ? (parsed as Record<string, HomeRecord>) : {};
  } catch {
    return {};
  }
}

function writeHomes(homes: Record<string, HomeRecord>): void {
  try { localStorage.setItem(HOMES_KEY, JSON.stringify(homes)); } catch { /* storage full or blocked: cleanup retries next push */ }
}

function keyFor(cfg: BridgeConfig): string {
  return cfg.nodeId || cfg.id;
}

/** Machines on record as holding `t` (its old home, earlier holders, ones not yet cleaned) that hold no part now. */
function staleHolders(t: Timeline, parts: readonly TimelinePart[]): string[] {
  const prev = readHomes()[t.id];
  if (!prev || prev.home === '') return [];
  const now = new Set(parts.map((p) => p.cfg.id));
  const keys = new Set<string>([...prev.stale, ...(prev.holders ?? [prev.home])]);
  return [...keys].filter((key) => { const cfg = resolveBridge(key); return !!cfg && !now.has(cfg.id); });
}

/** After a push round: make sure no machine that holds no part of `t` keeps a copy (a home that moved, a preview of
 *  a light owner that is back). Anything that cannot be cleaned right now stays listed and is retried on the next push:
 *  a machine that is off, or one still PLAYING `t` (deleting a running copy would stop it mid-song). */
async function cleanUpStaleCopies(t: Timeline, parts: readonly TimelinePart[]): Promise<void> {
  const home = timelinePlacement(t).home;
  if (!home) return;
  const stillStale: string[] = [];
  for (const key of staleHolders(t, parts)) {
    const old = resolveBridge(key)!;
    if (!connected(old) || transportRun(old.id)?.timelineId === t.id) { stillStale.push(key); continue; }
    const results = await deleteTimelineOnBridge(t.id, old.id);
    if (results.some((r) => r.kind !== 'ok')) { stillStale.push(key); continue; }
    // That machine no longer holds it, so what was last pushed there is not
    // there any more. Kept, it made a home that moved BACK with the same
    // content (a lane muted and unmuted, an undo) skip the push as "already
    // current" - autosync and Play alike - and the machine never got it
    // (found by `timeline_v2_multi_select_and_mute`, 2026-10-02).
    pushedJson.delete(pushedKey(t.id, old.id));
  }
  const homes = readHomes();
  homes[t.id] = { home: keyFor(home), holders: parts.map((p) => keyFor(p.cfg)), stale: stillStale };
  writeHomes(homes);
}

/** Forget everything this module knows about a timeline - the editor's
 *  Delete, after it has tried to remove the timeline from every bridge.
 *  `unreachable` are the bridges that could not be reached: they are kept on
 *  record (with an empty `home`, meaning "deleted") and retried by
 *  {@link sweepPendingTimelineDeletes}, so a machine that was offline at the
 *  moment of the delete does not keep a stale copy forever. */
export function forgetTimelineSync(id: string, unreachable: BridgeConfig[] = []): void {
  const timer = timers.get(id);
  if (timer !== undefined) clearTimeout(timer);
  timers.delete(id);
  for (const key of [...pushedJson.keys()]) if (key.startsWith(`${id}::`)) pushedJson.delete(key);
  setEntries((prev) => {
    const next = { ...prev };
    delete next[id];
    return next;
  });
  const homes = readHomes();
  if (unreachable.length > 0) homes[id] = { home: '', stale: unreachable.map(keyFor) };
  else delete homes[id];
  writeHomes(homes);
}

/** Retry deletes that could not reach their machine at the time (a deleted
 *  timeline's record has an empty `home`). Cheap when there is nothing
 *  pending - one localStorage read. Called on every auto-push and when the
 *  editor opens; never throws. */
export async function sweepPendingTimelineDeletes(): Promise<void> {
  const homes = readHomes();
  let changed = false;
  for (const [id, rec] of Object.entries(homes)) {
    if (rec.home !== '') continue;
    const left: string[] = [];
    for (const key of rec.stale) {
      const cfg = resolveBridge(key);
      if (!cfg) continue; // unpaired since: nothing left to clean there
      if (!connected(cfg)) { left.push(key); continue; }
      try {
        const results = await deleteTimelineOnBridge(id, cfg.id);
        if (results.some((r) => r.kind !== 'ok')) left.push(key);
      } catch {
        left.push(key);
      }
    }
    if (left.length === 0) delete homes[id];
    else homes[id] = { home: '', stale: left };
    changed = true;
  }
  if (changed) writeHomes(homes);
}

/** The sync pill after a parts push: the same rules as {@link applyPushOutcome}, over every computer pushed. Exported
 *  for a launch that pushed the parts itself. */
export function applyPartsOutcome(t: Timeline, results: FireResult[]): boolean {
  if (results.some((r) => r.newerCopy !== undefined)) {
    patchEntry(t.id, { state: 'idle', lastError: null, lastWarning: 'The computer has a newer copy of this timeline; this device updates when it syncs.' });
    return false;
  }
  const fail = results.find((r) => r.kind !== 'ok');
  if (fail) {
    if (fail.kind === 'unsupported-step') {
      const hasSteps = t.lanes.some((lane) => lane.steps.length > 0);
      patchEntry(t.id, { state: 'idle', lastError: null, lastWarning: hasSteps ? (fail.detail ?? null) : null });
      return false;
    }
    patchEntry(t.id, { state: 'error', lastError: plainFireReason(fail) });
    return false;
  }
  const warnings = results.map((r) => r.partialDropWarning).filter((w): w is string => !!w);
  patchEntry(t.id, { state: 'saved', lastSavedAt: Date.now(), lastError: null, lastWarning: warnings.length > 0 ? warnings.join(' ') : null });
  return true;
}

/** Push one timeline now, to every computer holding a part of it, updating its sync state. Used by the debounced
 *  auto-push below. */
export async function pushTimelineNow(t: Timeline): Promise<boolean> {
  if (pushInFlight.has(t.id)) { pushAgain.add(t.id); return false; }
  const { parts } = timelineParts(t);
  if (!parts.some((p) => connected(p.cfg))) { waitingForHome.add(t.id); return false; }

  pushInFlight.add(t.id);
  notePushStarted(t.id);
  try {
    const { results } = await pushTimelineParts(t);
    const ok = applyPartsOutcome(t, results);
    void sweepPendingTimelineDeletes();
    return ok;
  } catch (err) {
    patchEntry(t.id, { state: 'error', lastError: err instanceof Error ? err.message : String(err) });
    return false;
  } finally {
    pushInFlight.delete(t.id);
    if (pushAgain.delete(t.id)) { const latest = getTimeline(t.id); if (latest) void pushOrLive(liveDefinition(latest)); }
  }
}

/** Push `t` to its home: LIVE when it is running there, so the copy that is playing changes too (2026-10-16: an edit
 *  to a playing song was pushed for its next play while the pill said "saved"). */
function pushOrLive(t: Timeline): Promise<unknown> {
  return timelineRunning(t.id) ? pushTimelineLiveNow(t) : pushTimelineNow(t);
}

// ── A running launch's Loop override (audit 2026-11-03 C-3) ─────────────────
// A Launcher whose Loop overrides the timeline's own ("Loop: On" over a song
// that ends) pushes the timeline CARRYING that loop right before the start
// (`launcherDefinition`). Every automatic push after it - a project edit, a
// mute, a computer coming back - re-sent the timeline's OWN loop, and since the
// timeline was running it went live: the override was undone mid-song and the
// song stopped at its end (or looped when told not to). The override is
// remembered for as long as that run plays and carried on every automatic push.
const launchLoop = new Map<string, { loops: boolean; seen: number }>();
/** A press waits for its bar line (up to "4 bars" at a slow tempo) before the
 *  run shows, so an override is kept this long with nothing running (before
 *  its start, and after its stop) before it is dropped; after that, a start by
 *  a pedal or a Stream Deck key plays the timeline's own Loop. */
const LAUNCH_LOOP_GRACE_MS = 30_000;

/** The Launcher started `id` with this Loop (null = the timeline's own; any
 *  other Play clears it, `launch.ts`). */
export function noteLaunchLoop(id: string, loops: boolean | null): void {
  if (loops === null) launchLoop.delete(id);
  else launchLoop.set(id, { loops, seen: Date.now() });
}

/** What OPBridge should hold for `t` NOW: `t`, or `t` with the Loop its running
 *  launch was started with. An override outlives no run. */
export function liveDefinition(t: Timeline): Timeline {
  const o = launchLoop.get(t.id);
  if (!o) return t;
  if (timelineRunning(t.id)) o.seen = Date.now();
  else if (Date.now() - o.seen > LAUNCH_LOOP_GRACE_MS) { launchLoop.delete(t.id); return t; }
  // Kept while it waits for its bar line too: OPBridge takes the definition
  // it holds when the start lands, so a plain push in that window lost it.
  return timelineLoops(t) === o.loops ? t : { ...t, loop: o.loops };
}

/** What {@link pushTimelineLiveNow} achieved: `live` = the home bridge
 *  replaced its running copy (`copies` of them; 0 = it was not playing);
 *  `next-play` = the bridge is too old to touch a running timeline, so the
 *  change applies the next time it plays; `not-sent` = no reachable home or
 *  the push failed (the sync pill says why). */
export type LivePushOutcome = { kind: 'live'; copies: number } | { kind: 'next-play' } | { kind: 'not-sent' };

/** Push NOW, skipping the debounce, and replace the copy OPBridge is playing
 *  (`?live=true`, S306) - for a mute or solo, which must take effect at once
 *  ("a light lane muted while the song plays goes OFF at once",
 *  `.notepads/timeline-lights-master.md`). Any pending debounced push of the
 *  same timeline is dropped: this one carries the same content. A push
 *  already in flight is waited for, so the live one lands last. */
export async function pushTimelineLiveNow(given: Timeline): Promise<LivePushOutcome> {
  // A mute while a Launcher's Loop-overridden run plays keeps that Loop (C-3).
  const t = liveDefinition(given);
  const pending = timers.get(t.id);
  if (pending !== undefined) { clearTimeout(pending); timers.delete(t.id); }
  if (!timelineParts(t).parts.some((p) => connected(p.cfg))) return { kind: 'not-sent' };
  for (let i = 0; i < 40 && pushInFlight.has(t.id); i++) await new Promise((r) => setTimeout(r, 50));

  pushInFlight.add(t.id);
  notePushStarted(t.id);
  try {
    // Every part: a light lane muted while the song plays must go quiet on the computer that owns the light.
    const { results, liveCopies } = await pushTimelineParts(t, { live: true });
    if (results.length === 0 || !applyPartsOutcome(t, results)) return { kind: 'not-sent' };
    return liveCopies === undefined ? { kind: 'next-play' } : { kind: 'live', copies: liveCopies };
  } catch (err) {
    patchEntry(t.id, { state: 'error', lastError: err instanceof Error ? err.message : String(err) });
    return { kind: 'not-sent' };
  } finally {
    pushInFlight.delete(t.id);
  }
}

/** Debounce a push for this timeline - call on every edit (name/section/
 *  lane/step mutation). Marks `pending` immediately for instant UI
 *  feedback, then actually pushes `DEBOUNCE_MS` after the LAST call for
 *  this id. No-ops (stays `saved`) if the content hasn't actually changed
 *  since the last successful push - a resize/re-render that doesn't touch
 *  the timeline's own content must never flap the indicator. */
export function scheduleTimelineSync(given: Timeline): void {
  const t = liveDefinition(given);
  // 2026-09-17 - this read `pushedJson.get(t.id)`, a key shape the only
  // writer never produces (it writes `id::bridgeId`), so the de-dupe this
  // function documents had never once fired. 2026-09-29 - keyed on the
  // timeline's HOME now, the bridge the push below actually goes to.
  // 2026-10-28 - on every computer holding a part of it.
  if (timelinePartsCurrent(t)) return;

  patchEntry(t.id, { state: 'pending' });
  const existing = timers.get(t.id);
  if (existing !== undefined) clearTimeout(existing);
  timers.set(t.id, setTimeout(() => {
    timers.delete(t.id);
    // A live push (a mute) may have landed this exact content meanwhile.
    const latest = liveDefinition(getTimeline(t.id) ?? t);
    if (timelinePartsCurrent(latest)) { patchEntry(t.id, { state: 'saved' }); return; }
    void pushOrLive(latest);
  }, DEBOUNCE_MS));
}

let autoPushInstalled = false;
/**
 * Every change to a timeline reaches OPBridge, whoever made it (2026-10-16, the reliability audit). Only the OPEN
 * editor scheduled pushes, so an undo outside it, an edit synced from another device, or a change to a button a
 * timeline presses (its CC, its port, its pedal) never reached OPBridge until someone pressed Launch, and OPBridge's
 * own triggers (a Pacer route, a Stream Deck key) kept playing the old compiled copy.
 *   - A timeline object that changes after start-up is scheduled (debounced, de-duplicated against what was sent).
 *   - A change to the open project is checked 1.5 s later against every timeline of it that was sent before: the
 *     compile is compared, so only a timeline whose wire really changed is pushed.
 *   - A timeline whose home was offline is pushed when that computer is back.
 */
export function installTimelineAutoPush(): void {
  if (autoPushInstalled) return;
  autoPushInstalled = true;
  createRoot(() => {
    let seen: Map<string, Timeline> | null = null;
    createEffect(on(timelines, (list) => {
      const prev = seen;
      seen = new Map(list.map((t) => [t.id, t]));
      if (!prev) return;
      for (const t of list) if (prev.get(t.id) !== t) scheduleTimelineSync(t);
    }));
    let projTimer: ReturnType<typeof setTimeout> | null = null;
    createEffect(on(activeProject, (proj) => {
      if (!proj) return;
      if (projTimer) clearTimeout(projTimer);
      projTimer = setTimeout(() => {
        projTimer = null;
        for (const t of timelines()) {
          if (t.projectId !== proj.id) continue;
          // A lighting timeline must be on its lights' computer whether or not it was sent from here (below).
          if (t.purpose !== 'lights' && ![...pushedJson.keys()].some((k) => k.startsWith(`${t.id}::`))) continue;
          scheduleTimelineSync(t);
        }
      }, DEBOUNCE_MS);
    }, { defer: true }));
    createEffect(() => {
      const anyUp = bridges().some((b) => getConnection(b.id)?.status() === 'connected');
      if (!anyUp || waitingForHome.size === 0) return;
      for (const id of [...waitingForHome]) { const t = getTimeline(id); if (t) void pushTimelineNow(t); else waitingForHome.delete(id); }
    });
    // Which computers are on decides the parts (2026-10-28): with a light's owner off the others preview its lights,
    // and once it is back they stop. So when the set changes, every timeline this tab has sent is checked again (the
    // compile is compared, so only a changed part is pushed). And every LIGHTING timeline of the open project goes to
    // the computer that owns its lights, on start-up too: it must be there to be chosen as the default lights (OPBridge
    // S340: "your four lighting timelines never reached STREAMPC"). Nothing else is pushed at start-up.
    let upKey = '';
    createEffect(() => {
      const up = bridges().filter((b) => getConnection(b.id)?.status() === 'connected').map((b) => b.id).sort();
      const key = up.join(',');
      if (key === upKey) return;
      const before = new Set(upKey ? upKey.split(',') : []);
      upKey = key;
      if (!key) return;
      const sent = new Set([...pushedJson.keys()].map((k) => k.slice(0, k.indexOf('::'))));
      // A computer that comes BACK may not hold what this tab last sent it (restarted, its data reset): forget it, so
      // its parts are sent again. A press is carried to it by another computer now, so a copy missing there would fail
      // silently - nothing on this device would hear about it.
      const back = up.filter((id) => !before.has(id));
      for (const id of back) for (const k of [...pushedJson.keys()]) if (k.endsWith(`::${id}`)) pushedJson.delete(k);
      untrack(() => {
        const proj = activeProject()?.id;
        for (const t of timelines()) {
          if (!sent.has(t.id) && !(t.purpose === 'lights' && t.projectId === proj)) continue;
          // Its part goes to a computer that is back AT ONCE, not after the debounce: OPBridge starts a computer that
          // links mid-song 4 s after the mesh hears it (S342 late join), and it finds nothing to play without its part.
          if (back.length > 0 && timelineParts(t).parts.some((p) => back.includes(p.cfg.id))) void pushTimelineNow(liveDefinition(t));
          else scheduleTimelineSync(t);
        }
      });
    });
  });
}
